/**
 * 使い方ガイドの配置計算（純粋関数だけ。DOMに触れない）
 * スマートフォン片手持ちを基準に、説明カードを下部タブのすぐ上へ固定し、次へボタンを毎回同じ位置に出します。
 * 目印がスクロールしてもカードに隠れる場合だけ、カードを目印の上へ持ち上げます（上下の入れ替えはしない）。
 * 暗幕は下部タブの上端で止め、タブは明るく操作できるままにします。
 * 暗幕は「穴を除いた最大4枚の長方形」で作り、同じ長方形がタップも受け止めます（見た目と当たり判定を一致させる）。
 */

/** 画面上の長方形（viewport座標、px） */
export type Rect = Readonly<{ top: number; left: number; width: number; height: number }>;

/** 上下の範囲（viewport座標、px） */
export type VerticalRange = Readonly<{ top: number; bottom: number }>;

/** 配置計算の入力（ガイド本体が毎フレーム測って渡す） */
export type OverlayLayoutInput = Readonly<{
  viewport: Readonly<{ width: number; height: number }>;
  /** 下部タブの上端。タブが無い画面（読み込み中・準備中）ではnull → 暗幕とカードの基準は画面の下端 */
  tabBarTop: number | null;
  /** 描画済みカードの自然な高さ（最大高さで切る前） */
  cardNaturalHeight: number;
  /** 目印の長方形。目印が無い・未発見ならnull */
  targetRect: Rect | null;
  /** 手順が目印を持つか（目印の無い歓迎・終わりの手順はカードを高くしてよい） */
  stepHasAnchor: boolean;
  /** 次に押してもらう下部タブの長方形 */
  nextTabRect: Rect | null;
  /** カードを持ち上げてよいか。目印へのスクロールが落ち着くまではfalse（スクロール途中の位置で上下させない） */
  allowLift: boolean;
}>;

/** 明るく抜く穴。markerEdge はカードを指す小さな印を付ける辺 */
export type OverlayHole = Rect & Readonly<{ markerEdge: 'top' | 'bottom' }>;

/** 配置計算の結果 */
export type OverlayLayout = Readonly<{
  /** カードの位置。bottom は viewport 下端からカード下端までの距離 */
  card: Readonly<{ left: number; width: number; maxHeight: number; bottom: number }>;
  /** カードを目印の上へ持ち上げたか */
  isCardLifted: boolean;
  /** 明るく抜く穴（目印＋余白、最小44x44、見せる範囲で切り取り済み）。無ければnull */
  hole: OverlayHole | null;
  /** 暗幕兼タップ受け止めの長方形（穴の周り最大4枚。穴が無ければ暗幕領域全体の1枚） */
  blockers: readonly Rect[];
  /** 目印を見せる範囲（カードと重ならない部分）。スクロール量の計算に使う */
  freeRegion: VerticalRange;
  /** 次に押してもらうタブを囲む枠 */
  tabRing: Rect | null;
}>;

/** 左右の余白 */
const CARD_SIDE_GUTTER_PX = 16;
/** カードの最大幅（広い画面でも読みやすい幅に留める） */
const CARD_MAX_WIDTH_PX = 480;
/** カードと下部タブ（または画面端）の隙間。タブの押し間違いを防ぐ */
const CARD_EDGE_GAP_PX = 8;
/** カードと穴の最小の隙間 */
const CARD_HOLE_GAP_PX = 12;
/** 持ち上げたカードの下端と目印の隙間。カードを指す印がこの隙間に収まる */
const LIFTED_CARD_GAP_PX = 24;
/** 持ち上げたカードに最低限残す高さ（見出しと操作が入る高さ）。これより低くなるなら持ち上げない */
const LIFTED_CARD_MIN_HEIGHT_PX = 160;
/** 穴の余白 */
const HOLE_PADDING_PX = 6;
/** 穴の最小寸法（タップ目標と同じ44px） */
const HOLE_MIN_SIZE_PX = 44;
/** 目印付き手順でカードに使ってよい高さ（viewportに対する割合）。残りを目印へ譲る */
const ANCHORED_CARD_MAX_HEIGHT_RATIO = 0.5;
/** 目印付き手順のカードの最大高さの下限（これより小さいと見出しと操作だけで埋まる） */
const ANCHORED_CARD_MIN_MAX_HEIGHT_PX = 220;
/** 目印付き手順でカードの上に残す高さ（ヘッダー約80px + 入力欄やボタン1つを見せる約116px） */
const ANCHORED_RESERVED_TOP_PX = 196;
/** 目印の無い手順でカードの上に残す高さ（ヘッダー） */
const ANCHORLESS_RESERVED_TOP_PX = 80;

/**
 * 長方形を広げ、最小寸法に満たなければ中心を保って広げる
 * @param {Rect} target - 元の長方形
 * @returns {Rect} 広げた長方形
 */
const padTarget = (target: Rect): Rect => {
  const width = Math.max(target.width + HOLE_PADDING_PX * 2, HOLE_MIN_SIZE_PX);
  const height = Math.max(target.height + HOLE_PADDING_PX * 2, HOLE_MIN_SIZE_PX);
  return {
    top: target.top + target.height / 2 - height / 2,
    left: target.left + target.width / 2 - width / 2,
    width,
    height,
  };
};

/**
 * 長方形を範囲で切り取る。残らなければnull
 * @param {Rect} source - 元の長方形
 * @param {VerticalRange} vertical - 上下の範囲
 * @param {number} viewportWidth - 画面の幅
 * @returns {Rect | null} 切り取った長方形
 */
const clipRect = (source: Rect, vertical: VerticalRange, viewportWidth: number): Rect | null => {
  const top = Math.max(source.top, vertical.top);
  const bottom = Math.min(source.top + source.height, vertical.bottom);
  const left = Math.max(source.left, 0);
  const right = Math.min(source.left + source.width, viewportWidth);
  if (bottom - top < 1 || right - left < 1) {
    return null;
  }
  return { top, left, width: right - left, height: bottom - top };
};

/**
 * 領域から穴を除いた長方形（最大4枚）を返す
 * @param {Rect} area - 暗幕の領域
 * @param {Rect | null} hole - 穴
 * @returns {Rect[]} 暗幕の長方形
 */
const subtractHole = (area: Rect, hole: Rect | null): Rect[] => {
  if (!hole) {
    return [area];
  }
  const holeBottom = hole.top + hole.height;
  const holeRight = hole.left + hole.width;
  const areaBottom = area.top + area.height;
  const areaRight = area.left + area.width;
  return [
    { top: area.top, left: area.left, width: area.width, height: hole.top - area.top },
    { top: holeBottom, left: area.left, width: area.width, height: areaBottom - holeBottom },
    { top: hole.top, left: area.left, width: hole.left - area.left, height: hole.height },
    { top: hole.top, left: holeRight, width: areaRight - holeRight, height: hole.height },
  ].filter((blocker) => blocker.width > 0 && blocker.height > 0);
};

/**
 * カードと穴と暗幕の配置を計算する
 * @param {OverlayLayoutInput} input - 測定値
 * @returns {OverlayLayout} 配置
 */
export const computeOverlayLayout = (input: OverlayLayoutInput): OverlayLayout => {
  const { viewport } = input;
  const overlayBottom = input.tabBarTop ?? viewport.height;
  const width = Math.min(viewport.width - CARD_SIDE_GUTTER_PX * 2, CARD_MAX_WIDTH_PX);
  const left = (viewport.width - width) / 2;
  const maxHeight = input.stepHasAnchor
    ? Math.max(
      ANCHORED_CARD_MIN_MAX_HEIGHT_PX,
      Math.min(viewport.height * ANCHORED_CARD_MAX_HEIGHT_RATIO, overlayBottom - ANCHORED_RESERVED_TOP_PX),
    )
    : Math.max(ANCHORED_CARD_MIN_MAX_HEIGHT_PX, overlayBottom - CARD_EDGE_GAP_PX * 2 - ANCHORLESS_RESERVED_TOP_PX);
  const cardHeight = Math.min(input.cardNaturalHeight, maxHeight);
  const dockedBottomY = overlayBottom - CARD_EDGE_GAP_PX;
  const dockedFreeRegion: VerticalRange = { top: 0, bottom: dockedBottomY - cardHeight - CARD_HOLE_GAP_PX };

  const padded = input.targetRect ? padTarget(input.targetRect) : null;
  const liftedBottomY = padded ? padded.top - LIFTED_CARD_GAP_PX : 0;
  const liftedMaxHeight = Math.min(cardHeight, liftedBottomY - CARD_EDGE_GAP_PX);
  const shouldLift = Boolean(
    padded
    && input.allowLift
    && padded.top + padded.height > dockedFreeRegion.bottom
    && liftedMaxHeight >= LIFTED_CARD_MIN_HEIGHT_PX,
  );

  const freeRegion: VerticalRange = shouldLift
    ? { top: liftedBottomY + CARD_HOLE_GAP_PX, bottom: overlayBottom }
    : dockedFreeRegion;
  const clipped = padded ? clipRect(padded, freeRegion, viewport.width) : null;
  const hole: OverlayHole | null = clipped ? { ...clipped, markerEdge: shouldLift ? 'top' : 'bottom' } : null;

  return {
    card: shouldLift
      ? { left, width, maxHeight: liftedMaxHeight, bottom: viewport.height - liftedBottomY }
      : { left, width, maxHeight, bottom: viewport.height - dockedBottomY },
    isCardLifted: shouldLift,
    hole,
    blockers: subtractHole({ top: 0, left: 0, width: viewport.width, height: overlayBottom }, clipped),
    freeRegion,
    tabRing: input.nextTabRect,
  };
};

/**
 * 目印を見せたい範囲へ入れるための scrollTop の差分を返す
 * 目印が範囲より低ければ中央へ、高ければ上端を範囲の上端へ合わせる。既に入っていれば0。
 * スクロール領域の見えている部分と見せたい範囲の重なりだけを使う。
 * @param {{targetRect: Rect, scrollerRect: Rect, freeRegion: VerticalRange}} params - 測定値
 * @returns {number} scrollTopへ足す量（px、正で下へ）
 */
export const computeScrollAdjustment = (params: Readonly<{
  targetRect: Rect;
  scrollerRect: Rect;
  freeRegion: VerticalRange;
}>): number => {
  const { targetRect, scrollerRect, freeRegion } = params;
  const visibleTop = Math.max(scrollerRect.top, freeRegion.top);
  const visibleBottom = Math.min(scrollerRect.top + scrollerRect.height, freeRegion.bottom);
  const space = visibleBottom - visibleTop;
  if (space <= 0) {
    return 0;
  }
  if (targetRect.top >= visibleTop && targetRect.top + targetRect.height <= visibleBottom) {
    return 0;
  }
  const desiredTop = targetRect.height <= space ? visibleTop + (space - targetRect.height) / 2 : visibleTop;
  return targetRect.top - desiredTop;
};
